import { ApplicationEntity } from "@/model/entity/application-entity"
import { ColReqAddress } from "@/model/entity/collection-request/col-req-address"
import { PickupData } from "@/model/entity/collection-request/pickup-data"
import { FieldTypeEnum, FieldDescriptor } from "../field-descriptor"
import { Employee } from "@/model/entity/employee/employee"
import { colReqStatusEnum } from "@/model/enum/col-req-status-enum"
import {Expose, Type} from "class-transformer"
import {ShipmentParcelShop} from "@/model/entity/shipment/shipment-parcel-shop"

export class CollectionRequest extends ApplicationEntity {
  pickupAddress?: ColReqAddress

  deliveryAddress?: ColReqAddress

  pickupData?: PickupData

  @Type(() => ShipmentParcelShop)
  shipmentParcelShop?: ShipmentParcelShop

  @FieldDescriptor({
    fieldType: FieldTypeEnum.SELECT,
    fieldPrettyName: "entity.collection_request.pickup_status",
    selectionFieldDescription: {
      displayKey: "i18nKey",
      valueKey: "value",
      values: colReqStatusEnum,
      i18n: true
    },
    isFilterable: true
  })
  statusId?: number

  @FieldDescriptor({
    fieldType: FieldTypeEnum.DATE,
    fieldPrettyName: "entity.collection_request.entry_date",
    isFilterable: true,
    hasTime: true,
    columnWidth: "90",
    sortable: true,
    defaultSort: "descending"
  })
  createdDate?: Date

  @Expose({ name: "parcelNumber" })
  _parcelNumber?: number

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.parcel.parcel_number",
    columnWidth: "120",
    isFilterable: true
  })
  get parcelNumber() {
    return exists(this._parcelNumber) ? this._parcelNumber!.toString().padStart(14, "0") : ""
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.reference",
    columnWidth: "240",
    isFilterable: true
  })
  reference?: number

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.order_number",
    columnWidth: "240",
    isFilterable: true
  })
  orderNumber?: number

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.pickup_name",
    columnWidth: "55",
    isFilterable: true
  })
  get pickupName(): string | undefined {
    if (!exists(this.pickupAddress!)) {
      return undefined
    }
    return this.pickupAddress!.name
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.pickup_name2",
    columnWidth: "55",
    isFilterable: true
  })
  get pickupName2(): string | undefined {
    if (!exists(this.pickupAddress!)) {
      return undefined
    }
    return this.pickupAddress!.name2
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.pickup_street",
    columnWidth: "55",
    isFilterable: true
  })
  get pickupStreet(): string | undefined {
    if (!exists(this.pickupAddress!)) {
      return undefined
    }
    return this.pickupAddress!.street
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.pickup_house_number",
    columnWidth: "55",
    isFilterable: true
  })
  get pickupHouseNumber(): string | undefined {
    if (!exists(this.pickupAddress!)) {
      return undefined
    }
    return this.pickupAddress!.houseNumber
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.pickup_city",
    columnWidth: "55",
    isFilterable: true
  })
  get pickupCity(): string | undefined {
    if (!exists(this.pickupAddress!)) {
      return undefined
    }
    return this.pickupAddress!.city
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.info1",
    columnWidth: "55",
    isFilterable: true
  })
  get info1(): string | undefined {
    if (!exists(this.pickupData!)) {
      return undefined
    }
    return this.pickupData!.info1
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.info2",
    columnWidth: "55",
    isFilterable: true
  })
  get info2(): string | undefined {
    if (!exists(this.pickupData!)) {
      return undefined
    }
    return this.pickupData!.info2
  }

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.employee",
    displayed: false
  })
  employee?: Employee

  parcelStatusId?: string

  parcelAdditionalCode?: string

  parcelUpdateDate?: number

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.service_code",
    columnWidth: "55",
    isFilterable: true
  })
  serviceCode?: string

  @FieldDescriptor({
    fieldType: FieldTypeEnum.TEXT,
    fieldPrettyName: "entity.collection_request.pudo_id",
    columnWidth: "55",
    isFilterable: true
  })
  get pudoId(): string | undefined {
    if (!exists(this.shipmentParcelShop!)) {
      return undefined
    }
    return this.shipmentParcelShop!.pudoId
  }

  constructor(
    pickupAddress: ColReqAddress,
    deliveryAddress: ColReqAddress,
    pickupData: PickupData,
    shipmentParcelShop: ShipmentParcelShop,
    serviceCode?: string,
    createdDate?: Date,
    id?: number
  ) {
    super(id)
    this.pickupAddress = pickupAddress
    this.deliveryAddress = deliveryAddress
    this.pickupData = pickupData
    this.shipmentParcelShop = shipmentParcelShop
    this.serviceCode = serviceCode
    this.createdDate = createdDate
  }
}
